import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { invitationsApi } from '../../api/client';
import Logo from '../Logo/Logo';
import './Auth.css';

const RegisterInvitation = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setValidatingToken(true);
      await invitationsApi.validate(token);
      setTokenValid(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invitation invalide ou expirée');
      setTokenValid(false);
    } finally {
      setValidatingToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Vérifier que les mots de passe correspondent
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    // Vérifier la longueur du mot de passe
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const response = await invitationsApi.register(token, formData.username, formData.password);

      console.log('✅ Inscription réussie:', response.data);

      // Stocker le token et les infos utilisateur
      login(response.data.user, response.data.access_token);

      // Rediriger vers le profil pour compléter les informations
      navigate('/profil', { state: { firstLogin: true } });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <Logo variant="login" size="xlarge" />
          <h1>Validation...</h1>
          <p className="auth-subtitle">Vérification de l'invitation en cours...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <Logo variant="login" size="xlarge" />
          <h1>Invitation invalide</h1>
          <div className="auth-error">{error}</div>
          <p className="auth-subtitle">
            Cette invitation n'est pas valide ou a déjà été utilisée.
            Contactez un administrateur pour obtenir une nouvelle invitation.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-auth"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Logo variant="login" size="xlarge" />
        <h1>Créer votre compte</h1>
        <p className="auth-subtitle">Bienvenue ! Complétez les informations ci-dessous</p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">Nom d'utilisateur *</label>
            <input
              id="username"
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              autoComplete="username"
              placeholder="Votre pseudo"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Mot de passe *</label>
            <input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              autoComplete="new-password"
              placeholder="Minimum 6 caractères"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmer le mot de passe *</label>
            <input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              autoComplete="new-password"
              placeholder="Retapez votre mot de passe"
            />
          </div>

          <button type="submit" className="btn-auth" disabled={loading}>
            {loading ? 'Création du compte...' : 'Créer mon compte'}
          </button>
        </form>

        <p className="auth-note">
          💡 Après la création de votre compte, vous serez redirigé vers votre profil pour compléter vos informations.
        </p>
      </div>
    </div>
  );
};

export default RegisterInvitation;
